import {
  Controller,
  FilterBuilder,
  FilterBuilderFieldDefinition,
  FilterBuilderFieldsType,
  useFormContext,
  type FilterBuilderAllowedOperatorsMapping,
} from "@validereinc/common-components";
import {
  FormSchemaQuestionDisplayType,
  FormSchemaQuestionType,
} from "@validereinc/domain";
import { Forms } from "@validereinc/domain-controllers";
import { LogicalOperators } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { get, isEmpty } from "es-toolkit/compat";
import React, { useMemo, useState } from "react";
import styles from "./QuestionConditions.module.scss";

const { getPickListValuesWithConditionalOverrides } = Forms.logic;

const cx = classNames.bind(styles);

const equalityOperatorValue = "=";
const dataTypeOperatorInputTypeMapping: FilterBuilderAllowedOperatorsMapping = {
  number: {
    [equalityOperatorValue]: "number-input",
    $lt: "number-input",
    $lte: "number-input",
    $gt: "number-input",
    $gte: "number-input",
  },
  integer: {
    [equalityOperatorValue]: "number-input",
    $lt: "number-input",
    $lte: "number-input",
    $gt: "number-input",
    $gte: "number-input",
  },
  string: {
    [equalityOperatorValue]: "string-input",
  },
  "pick-list": {
    $in: "picklist-multi-input",
  },
  "custom-pick-list": {
    $in: "custom-pick-list-input",
  },
  date: {
    [equalityOperatorValue]: "date-input",
    $eq: "date-input",
    $lt: "date-input",
    $lte: "date-input",
    $gt: "date-input",
    $gte: "date-input",
  },
  boolean: {
    [equalityOperatorValue]: "boolean-input",
    $eq: "boolean-input",
    $ne: "boolean-input",
  },
  mixed: {},
};

/**
 * Normalizes a filter object so that each condition within `$or`/`$and` arrays
 * has at most one key, as required by FilterBuilder. Multi-key conditions are
 * implicitly `$and` conjunctions and are wrapped in a `$and` sub-group.
 */
const normalizeFilterForFilterBuilder = (
  filter: Record<string, unknown>
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(filter)) {
    if (
      (key === LogicalOperators.or || key === LogicalOperators.and) &&
      Array.isArray(value)
    ) {
      result[key] = value.map((condition: Record<string, unknown>) => {
        const normalized = normalizeFilterForFilterBuilder(condition);
        const keys = Object.keys(normalized);

        if (keys.length <= 1) return normalized;

        return {
          [LogicalOperators.and]: keys.map((k) => ({ [k]: normalized[k] })),
        };
      });
    } else {
      result[key] = value;
    }
  }

  return result;
};

/**
 * Denormalizes a filter object produced by FilterBuilder back into the format
 * expected by the backend. Inlines `$and` sub-groups that were introduced by
 * {@link normalizeFilterForFilterBuilder} by merging their single-key entries
 * into one multi-key condition object.
 */
const denormalizeFilterFromFilterBuilder = (
  filter: Record<string, unknown>
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(filter)) {
    if (
      (key === LogicalOperators.or || key === LogicalOperators.and) &&
      Array.isArray(value)
    ) {
      result[key] = value.map((condition: Record<string, unknown>) => {
        const denormalized = denormalizeFilterFromFilterBuilder(condition);
        const andValue = denormalized[LogicalOperators.and];

        if (!Array.isArray(andValue)) return denormalized;

        const merged: Record<string, unknown> = {};

        for (const entry of andValue as Array<Record<string, unknown>>) {
          Object.assign(merged, denormalizeFilterFromFilterBuilder(entry));
        }

        const { [LogicalOperators.and]: _, ...rest } = denormalized;
        return { ...rest, ...merged };
      });
    } else {
      result[key] = value;
    }
  }

  return result;
};

export const QuestionConditions = ({
  fieldKey,
  defaultConditions,
  questions,
  handleRemoveLastCondition,
  isRequired = false,
}: {
  fieldKey?: string;
  questions: FormSchemaQuestionType[];
  defaultConditions?: FormSchemaQuestionDisplayType["conditions"];
  handleRemoveLastCondition?: () => void;
  isRequired?: boolean;
}) => {
  const form = useFormContext<FormSchemaQuestionDisplayType>();

  const normalizedDefaultConditions = useMemo(
    () =>
      normalizeFilterForFilterBuilder(
        (defaultConditions ?? {}) as Record<string, unknown>
      ),
    [defaultConditions]
  );

  const [filter, setFilter] = useState(normalizedDefaultConditions);

  const filterBuilderFields: FilterBuilderFieldsType = Object.fromEntries(
    questions
      .filter((q) => "question_id" in q)
      .map((q) => {
        let definition: FilterBuilderFieldDefinition | undefined = undefined;

        if (q.type !== "question") return [];

        if (
          q.data_type === "boolean" ||
          q.data_type === "integer" ||
          q.data_type === "number"
        ) {
          definition = { data_type: q.data_type, display_name: q.prompt };
        }

        if (q.data_type === "date") {
          definition = {
            data_type: "date",
            display_name: q.prompt,
            format: "yyyy-MM-dd",
          };
        }

        if (q.data_type === "pick-list" || q.data_type === "multi-pick-list") {
          definition = {
            data_type: "pick-list",
            display_name: q.prompt,
            pick_list_options: getPickListValuesWithConditionalOverrides(q),
          };
        }

        if (!definition) return [];

        return [q.question_id, definition];
      })
      .filter((value) => !!value[1])
  );

  const fieldName = fieldKey ? `${fieldKey}conditions` : "conditions";

  return (
    <Controller
      name={fieldName as any}
      control={form.control}
      defaultValue={defaultConditions ?? {}}
      shouldUnregister={false}
      rules={
        isRequired
          ? {
              validate: {
                notEmpty: (value) => {
                  if (!value || typeof value !== "object") {
                    return "At least one condition must be defined";
                  }
                  const hasConditions = Object.keys(value).length > 0;
                  return (
                    hasConditions || "At least one condition must be defined"
                  );
                },
              },
            }
          : undefined
      }
      render={({ field }) => (
        <>
          <FilterBuilder
            fields={filterBuilderFields}
            filter={normalizeFilterForFilterBuilder(
              (field.value ?? filter) as Record<string, unknown>
            )}
            variant="vertical"
            setFilter={setFilter}
            onFilterChange={(newFilter) => {
              const denormalized = denormalizeFilterFromFilterBuilder(
                newFilter as Record<string, unknown>
              );
              field.onChange(denormalized);
              field.onBlur();
              setFilter(newFilter);
              if (isEmpty(newFilter)) handleRemoveLastCondition?.();
            }}
            maxDepth={1}
            operatorMappingOverride={dataTypeOperatorInputTypeMapping}
          />
          {get(form.formState.errors, fieldName) && (
            <p className={cx("error-message")}>
              {get(form.formState.errors, fieldName ?? "conditions")?.message ??
                ""}
            </p>
          )}
        </>
      )}
    />
  );
};
